import { Routes, Route, Navigate, useLocation } from 'react-router';
import { useEffect } from 'react';
import { AuthProvider } from '@/contexts/AuthContext';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import { Layout } from '@/components/layout/Layout';
import { LoginPage } from '@/pages/LoginPage';
import { AuthCallbackPage } from '@/pages/AuthCallbackPage';
import { DashboardPage } from '@/pages/DashboardPage';
import { DiskImagesPage } from '@/pages/DiskImagesPage';
import { DiskImageDetailPage } from '@/pages/DiskImageDetailPage';
import { CreateDiskImagePage } from '@/pages/CreateDiskImagePage';
import { SandboxesPage } from '@/pages/SandboxesPage';
import { SandboxDetailPage } from '@/pages/SandboxDetailPage';
import { CreateSandboxPage } from '@/pages/CreateSandboxPage';
import { CreateCopilotSandboxPage } from '@/pages/CreateCopilotSandboxPage';
import { CreateClaudeSandboxPage } from '@/pages/CreateClaudeSandboxPage';
import { CreateBatchSandboxPage } from '@/pages/CreateBatchSandboxPage';
import { SandboxGroupDetailPage } from '@/pages/SandboxGroupDetailPage';
import { CreateSandboxGroupPage } from '@/pages/CreateSandboxGroupPage';
import { SandboxGroupsPage } from '@/pages/SandboxGroupsPage';
import { AppsPage } from '@/pages/AppsPage';
import { AppDetailPage } from '@/pages/AppDetailPage';
import { CreateAppPage } from '@/pages/CreateAppPage';
import { SnapshotsPage } from '@/pages/SnapshotsPage';
import { VolumesPage } from '@/pages/VolumesPage';
import { VolumeDetailPage } from '@/pages/VolumeDetailPage';
import { CreateVolumePage } from '@/pages/CreateVolumePage';
import { SettingsPage } from '@/pages/SettingsPage';
import { MCPPage } from '@/pages/MCPPage';
import { GettingStartedPage } from '@/pages/GettingStartedPage';
import { ConnectorsPage } from '@/pages/ConnectorsPage';
import { GroupScopedConnectorsPage } from '@/pages/GroupScopedConnectorsPage';
import { AgentIdentitiesPage } from '@/pages/AgentIdentitiesPage';
import { CreateAgentIdentityPage } from '@/pages/CreateAgentIdentityPage';
import { LogsPage } from '@/pages/LogsPage';
import { MonitoringPage } from '@/pages/MonitoringPage';
import { TraceDetailPage } from '@/pages/TraceDetailPage';
import { ContentPackagesPage } from '@/pages/ContentPackagesPage';
import { SecretsPage } from '@/pages/SecretsPage';
import { ProjectsPage } from '@/pages/ProjectsPage';
import { CreateProjectPage } from '@/pages/CreateProjectPage';
import { DocsPage } from '@/pages/DocsPage';
import { NotFoundPage } from '@/pages/NotFoundPage';
import { config } from '@/config';

/**
 * Main application component with routing configuration.
 *
 * Route structure:
 * - /login - GitHub OAuth login page
 * - /auth/callback - OAuth callback handler
 * - /dashboard - Overview of resources (protected)
 * - /disk-images - List and manage disk images (protected)
 * - /disk-images/new - Create new disk image (protected)
 * - /disk-images/:id - View disk image details (protected)
 * - /sandboxes - List and manage sandboxes (protected)
 * - /sandboxes/new - Create new sandbox (protected)
 * - /sandboxes/:id - View sandbox details (protected)
 * - /settings - Application settings (protected)
 */
function App() {
  const location = useLocation();

  // Scroll to top on route change for better UX
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  return (
    <AuthProvider>
      <Routes>
        {/* Public routes */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/auth/callback" element={<AuthCallbackPage />} />

        {/* Protected routes */}
        <Route
          path="/*"
          element={
            <ProtectedRoute>
              <Layout>
                <Routes>
                  {/* Redirect root to sandboxes */}
                  <Route path="/" element={<Navigate to="/sandboxes" replace />} />

                  {/* Dashboard */}
                  <Route path="/dashboard" element={<DashboardPage />} />

                  {/* Disk Images */}
                  <Route path="/disk-images" element={<DiskImagesPage />} />
                  <Route path="/disk-images/new" element={<CreateDiskImagePage />} />
                  <Route path="/disk-images/:id" element={<DiskImageDetailPage />} />

                  {/* Sandboxes */}
                  <Route path="/sandboxes" element={<SandboxesPage />} />
                  <Route path="/sandboxes/new" element={<CreateSandboxPage />} />
                  <Route path="/sandboxes/new/copilot" element={<CreateCopilotSandboxPage />} />
                  <Route path="/sandboxes/new/claude" element={<CreateClaudeSandboxPage />} />

                  <Route path="/sandboxes/batch" element={<CreateBatchSandboxPage />} />
                  <Route path="/sandboxes/:id" element={<SandboxDetailPage />} />

                  {/* Sandbox Groups */}
                  <Route path="/sandbox-groups" element={<SandboxGroupsPage />} />
                  <Route path="/sandbox-groups/new" element={<CreateSandboxGroupPage />} />
                  <Route
                    path="/sandbox-groups/:sandboxGroupId/sandboxes/:id"
                    element={<SandboxDetailPage />}
                  />
                  <Route path="/sandbox-groups/:id" element={<SandboxGroupDetailPage />} />

                  {/* Group-scoped resource views */}
                  <Route path="/sandbox-groups/:groupId/sandboxes" element={<SandboxesPage />} />
                  <Route
                    path="/sandbox-groups/:groupId/sandboxes/new"
                    element={<CreateSandboxPage />}
                  />
                  <Route
                    path="/sandbox-groups/:groupId/sandboxes/new/copilot"
                    element={<CreateCopilotSandboxPage />}
                  />
                  <Route
                    path="/sandbox-groups/:groupId/sandboxes/new/claude"
                    element={<CreateClaudeSandboxPage />}
                  />
                  <Route path="/sandbox-groups/:groupId/disk-images" element={<DiskImagesPage />} />
                  <Route
                    path="/sandbox-groups/:groupId/disk-images/:id"
                    element={<DiskImageDetailPage />}
                  />
                  <Route path="/sandbox-groups/:groupId/snapshots" element={<SnapshotsPage />} />
                  <Route path="/sandbox-groups/:groupId/volumes" element={<VolumesPage />} />
                  <Route
                    path="/sandbox-groups/:groupId/volumes/new"
                    element={<CreateVolumePage />}
                  />
                  <Route
                    path="/sandbox-groups/:groupId/volumes/:name"
                    element={<VolumeDetailPage />}
                  />
                  <Route
                    path="/sandbox-groups/:groupId/connectors"
                    element={<GroupScopedConnectorsPage />}
                  />

                  {/* Snapshots */}
                  <Route path="/snapshots" element={<SnapshotsPage />} />

                  {/* Volumes */}
                  <Route path="/volumes" element={<VolumesPage />} />
                  <Route path="/volumes/new" element={<CreateVolumePage />} />
                  <Route path="/volumes/:name" element={<VolumeDetailPage />} />

                  {/* Secrets */}
                  <Route path="/sandbox-groups/:groupId/secrets" element={<SecretsPage />} />
                  <Route path="/secrets" element={<SecretsPage />} />

                  {/* Projects */}
                  <Route path="/projects" element={<ProjectsPage />} />
                  <Route path="/projects/new" element={<CreateProjectPage />} />

                  {/* Agent Identities (preview only) */}
                  {config.isPreviewMode && (
                    <>
                      <Route path="/agent-identities" element={<AgentIdentitiesPage />} />
                      <Route path="/agent-identities/new" element={<CreateAgentIdentityPage />} />
                    </>
                  )}

                  {/* Apps (preview only) */}
                  {config.isPreviewMode && (
                    <>
                      <Route path="/apps" element={<AppsPage />} />
                      <Route path="/apps/new" element={<CreateAppPage />} />
                      <Route path="/apps/:id" element={<AppDetailPage />} />
                    </>
                  )}

                  {/* MCP */}
                  <Route path="/mcp" element={<MCPPage />} />

                  {/* CLI + SDKs */}
                  <Route path="/getting-started" element={<GettingStartedPage />} />

                  {/* Connectors */}
                  <Route path="/connectors" element={<ConnectorsPage />} />

                  {/* Debug Logs */}
                  <Route path="/logs" element={<LogsPage />} />
                  <Route path="/logs/traces/:traceId" element={<TraceDetailPage />} />

                  {/* Monitoring */}
                  <Route path="/monitoring" element={<MonitoringPage />} />

                  {/* Content Packages */}
                  <Route path="/content-packages" element={<ContentPackagesPage />} />
                  <Route
                    path="/sandbox-groups/:groupId/content-packages"
                    element={<ContentPackagesPage />}
                  />

                  {/* Settings */}
                  <Route path="/settings" element={<SettingsPage />} />

                  {/* Docs */}
                  <Route path="/docs" element={<DocsPage />} />
                  <Route path="/docs/*" element={<DocsPage />} />

                  {/* 404 */}
                  <Route path="*" element={<NotFoundPage />} />
                </Routes>
              </Layout>
            </ProtectedRoute>
          }
        />
      </Routes>
    </AuthProvider>
  );
}

export default App;
